import React, { useState, useRef, useEffect, useMemo } from 'react'
import { SecondaryButton } from 'src/components/Reusable/Buttons'
import { FlexRow, FlexColumn, FlexAlign } from 'src/components/Reusable/Flex'
import {
  FilePickerContainer,
  UploadIcon,
  FilePickerLabel,
  FilePickerContainer__label,
  FilePickerContainer__TextLight,
  FilePickerContainer__TextBold,
  FilePickerContainer__Text,
  HiddenFileInput,
  // TextIcon,
  CloseIcon,
  SelectedFileLabel,
  ErrorMessage,
} from './FilePicker.styles'

export const FilePicker = ({
  label,
  onValidateFile,
  onUploadFile,
  onRemoveFile,
  errorMessage,
  supportedFileTypes,
  maxFileSizeInKB = 100,
  initialValue = null,
}: {
  label?: string
  onUploadFile: (file: File | null) => void
  onValidateFile?: (file: File | null) => Promise<boolean>
  onRemoveFile?: (file: File | null) => void
  errorMessage?: string
  supportedFileTypes?: string[]
  maxFileSizeInKB?: number
  initialValue?: File | string | null
}): JSX.Element => {
  const [isDragging, setIsDragging] = useState(false)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [error, setError] = useState<string | null>(null)

  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleDrag = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setIsDragging(true)
  }

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setIsDragging(false)
  }

  const getExtension = (filename: string): string => {
    return filename.split('.').pop() || ''
  }

  const backgroundImage = useMemo(() => {
    return selectedFile && selectedFile.type.startsWith('image/')
      ? `url(${URL.createObjectURL(selectedFile)})`
      : initialValue
      ? `url(${initialValue})`
      : undefined
  }, [selectedFile, initialValue])

  useEffect(() => {
    if (errorMessage && error) {
      setError(errorMessage)
    }
  }, [errorMessage, error])

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setIsDragging(false)
    const files = e.dataTransfer.files
    updateHandler(
      files
        ? Array.from(files).filter((file) =>
            supportedFileTypes
              ? supportedFileTypes.includes(getExtension(file.name))
              : true,
          )
        : null,
    )
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    updateHandler(
      files
        ? Array.from(files).filter((file) =>
            supportedFileTypes
              ? supportedFileTypes.includes(getExtension(file.name))
              : true,
          )
        : null,
    )
  }

  const updateHandler = async (files: File[] | null) => {
    if (files && files.length > 0) {
      if (files[0] && files[0].size > maxFileSizeInKB * 1024) {
        setError(`File size exceeds ${maxFileSizeInKB}KB`)
        return
      }
      if (onValidateFile && !(await onValidateFile(files[0]))) {
        setError(errorMessage || 'Invalid file')
        return
      }
      setError(null)
      setSelectedFile(files[0] || null)
      try {
        onUploadFile(files[0] || null)
      } catch (error) {
        setError('Failed to upload file')
      }
    }
  }

  const handleBrowseFiles = () => {
    fileInputRef.current?.click()
  }
  return (
    <FlexColumn
      gap={8}
      align={FlexAlign.FLEX_START}
      justify={FlexAlign.FLEX_START}
      isFullWidth
    >
      {label && <FilePickerLabel>{label}</FilePickerLabel>}
      <FilePickerContainer
        onDragOver={handleDrag}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={isDragging ? 'isdragging' : ''}
        backgroundImage={backgroundImage}
      >
        {isDragging ? (
          <>
            <UploadIcon />
            <FilePickerContainer__label>
              Drag files here
            </FilePickerContainer__label>
          </>
        ) : (
          <>
            {selectedFile || backgroundImage ? (
              <FlexRow
                align={FlexAlign.CENTER}
                justify={FlexAlign.SPACE_BETWEEN}
                isFullWidth
              >
                <SelectedFileLabel>
                  {selectedFile && selectedFile.name}
                </SelectedFileLabel>
                <CloseIcon
                  onClick={() => {
                    setSelectedFile(null)
                    onRemoveFile?.(selectedFile)
                  }}
                />
              </FlexRow>
            ) : (
              <>
                <UploadIcon />
                <FilePickerContainer__label>
                  Drag files to upload
                </FilePickerContainer__label>
                <FilePickerContainer__Text>or</FilePickerContainer__Text>
                <SecondaryButton
                  onClick={() => handleBrowseFiles()}
                  text="Browse files"
                />

                <FlexRow
                  gap={4}
                  align={FlexAlign.CENTER}
                  justify={FlexAlign.CENTER}
                >
                  <FilePickerContainer__TextLight>
                    Max file size:
                  </FilePickerContainer__TextLight>
                  <FilePickerContainer__TextBold>
                    {maxFileSizeInKB}KB
                  </FilePickerContainer__TextBold>
                </FlexRow>
                {supportedFileTypes && supportedFileTypes.length > 0 && (
                  <>
                    <FlexRow
                      gap={4}
                      align={FlexAlign.CENTER}
                      justify={FlexAlign.CENTER}
                    >
                      <FilePickerContainer__TextLight>
                        Supported files:
                      </FilePickerContainer__TextLight>
                      <FilePickerContainer__TextBold>
                        {supportedFileTypes.join(', ')}
                      </FilePickerContainer__TextBold>
                    </FlexRow>
                  </>
                )}
                <HiddenFileInput
                  type="file"
                  ref={fileInputRef}
                  accept={
                    supportedFileTypes
                      ? supportedFileTypes.map((type) => `.${type}`).join(',')
                      : undefined
                  }
                  onChange={handleFileChange}
                />
              </>
            )}
          </>
        )}
        {error && error.length > 0 && <ErrorMessage>{error}</ErrorMessage>}
      </FilePickerContainer>
    </FlexColumn>
  )
}
